// Donation details — opened from CRM › Donations. Same look as the order
// details modal (appointments/components/order.js): header with age, id and
// status pill; sections of label / bold value separated by dividers.
// The donation doc is followed live, so a pending one flips to paid in place.
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Colxx } from '../../../components/common/CustomBootstrap';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';

export const DONATION_STATUS = {
  paid: { label: 'Paid', color: 'success' },
  pending: { label: 'Pending', color: 'info' },
  creating: { label: 'Starting', color: 'secondary' },
  failed: { label: 'Not completed', color: 'danger' },
  error: { label: 'Error', color: 'warning' },
};

// DubaiPay message.code values seen in QA
const RESULT_CODES = {
  0: 'Success',
  9: 'Failure (declined / invalid request)',
  10: 'Cancelled by the payer',
  11: 'Abandoned — payment page not completed',
};

// dashboard CSS stretches .badge — keep the pill hugging its text
export const PILL = { display: 'inline-block', width: 'auto', padding: '0.35rem 0.9rem' };

const fmt = (ms) => (ms ? moment(ms).format('DD MMM YYYY, hh:mm A') : '--');

const Field = ({ label, children, md = '6' }) => (
  <Colxx xxs="12" md={md}>
    <Label className="mb-0 text-semi-muted">{label}</Label>
    <p className="font-weight-bold">{children || '--'}</p>
  </Colxx>
);

const Copyable = ({ value }) => (value ? (
  <span>
    {value}
    <Button
      color="link"
      size="xs"
      className="p-0 ml-2 align-baseline"
      onClick={() => { navigator.clipboard.writeText(String(value)); createNotification('success', 'Copied', ''); }}
    >
      <i className="simple-icon-docs" />
    </Button>
  </span>
) : null);

const Divider = () => <div className="border-bottom p-3 mb-4" />;

const DonationModal = ({ donation, isOpen, dismiss }) => {
  const [live, setLive] = useState(donation);

  useEffect(() => {
    setLive(donation);
    if (!isOpen || !donation?.batchId) return undefined;
    const unsub = firestore().collection('donations').doc(donation.batchId).onSnapshot((snap) => {
      if (snap.exists) setLive((prev) => ({ ...prev, ...snap.data() }));
    }, () => { });
    return () => unsub();
  }, [isOpen, donation]);

  if (!live) return null;
  const status = DONATION_STATUS[live.status] || { label: live.status, color: 'secondary' };
  const booking = live.booking || {};
  const code = live.resultCode;

  return (
    <Modal isOpen={isOpen} toggle={dismiss} size="lg">
      <ModalHeader toggle={dismiss}>
        <div style={{ fontWeight: '600' }} className="mb-1">
          {live.createdAt ? `${moment(live.createdAt).fromNow(true)} ago` : ''}
        </div>
        <div className="font-weight-bold m-0 mb-2">
          Donation · AED {Number(live.amount || 0).toFixed(2)}
        </div>
        <Badge color={status.color} pill style={PILL}>{status.label}</Badge>
      </ModalHeader>

      <ModalBody>
        <h5>Donor</h5>
        <Row>
          <Field label="Customer Name:">{live.customerName}</Field>
          <Field label="Customer Number:">{live.customerPhone}</Field>
          <Field label="Email:">{live.customerEmail}</Field>
          <Field label="Customer ID:"><Copyable value={live.uid} /></Field>
        </Row>
        <div className="text-muted text-small mb-2">
          Sent to DubaiPay as an anonymous donor (no Emirates ID). DubaiPay may ask the donor for contact details on its page.
        </div>

        <Divider />
        <h5>Payment</h5>
        <Row>
          <Field label="Amount:">{`AED ${Number(live.amount || 0).toFixed(2)}`}</Field>
          <Field label="Status:"><Badge color={status.color} pill style={PILL}>{status.label}</Badge></Field>
          <Field label="Paid Via:">DubaiPay (Awqaf — Endowment and Minors Foundation)</Field>
          <Field label="DubaiPay Reference:"><Copyable value={live.degTrn} /></Field>
          <Field label="Payment Method:">{live.paymentMethod}</Field>
          <Field label="Result Code:">
            {code != null && code !== '' ? `${code} — ${RESULT_CODES[code] || 'See DubaiPay'}` : null}
          </Field>
          {live.resultText ? <Field label="DubaiPay Message:">{live.resultText}</Field> : null}
          {live.error ? <Field label="Error:" md="12"><span className="text-danger">{live.error}</span></Field> : null}
        </Row>

        <Divider />
        <h5>References</h5>
        <Row>
          <Field label="Batch ID:"><Copyable value={live.batchId} /></Field>
          <Field label="Transaction Ref (spTrn):"><Copyable value={live.spTrn} /></Field>
        </Row>
        <div className="text-muted text-small mb-2">
          Quote the DubaiPay reference or batch ID when checking a donation with Awqaf / DubaiPay.
        </div>

        <Divider />
        <h5>Linked Order</h5>
        {live.bookingId ? (
          <Row>
            <Field label="Order ID:">{live.orderNumber}</Field>
            <Field label="Service:">{booking.service_name}</Field>
            <Field label="Provider:">{booking.sp_name}</Field>
            <Field label="Appointment Date:">{fmt(booking.bookingDate)}</Field>
          </Row>
        ) : <p className="text-muted">Not linked to an order.</p>}

        <Divider />
        <h5>Timeline</h5>
        <Row>
          <Field label="Started:" md="4">{fmt(live.createdAt)}</Field>
          <Field label="Paid:" md="4">{fmt(live.paidAt)}</Field>
          <Field label="Last Updated:" md="4">{fmt(live.updatedAt)}</Field>
        </Row>
      </ModalBody>

      <ModalFooter>
        <Button color="primary" outline onClick={dismiss}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default DonationModal;
